/**
 * O rascunho da conclusão de UMA CCB na mesa (E74.F3; E180.F2, GUS-254 item 10, essencial da
 * GUS-46).
 *
 * Antes o rascunho vivia no estado do formulário, que remonta a cada troca de documento
 * (`key={docId}`) e se realinhava a cada releitura da mesa (o cartão do pacote relê a cada 4 s):
 * o texto digitado e não registrado sumia. Agora ele fica guardado por documento enquanto a tela
 * está aberta, e só se descarta quando o exame muda de base (concluído, reaberto ou verificado de
 * novo com outro grau). Cada documento lê só o seu; o rascunho nunca vale como conclusão
 * registrada — ele não sai do navegador.
 *
 * F-E180.F2-01: o rascunho é da sessão que o escreveu. Sair, ou entrar com outra conta na mesma
 * aba, descarta os guardados e recomeça o formulário montado; a troca de documento na mesma
 * sessão o preserva.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ExameEletronicoCcbData, GrauConvergencia } from '@/lib/exame_eletronico';
import { type SessionState, useSessionStore, useToken } from '@/stores/session';

export interface RascunhoDaDecisao {
  grau: GrauConvergencia | null;
  justificativa: string;
  conclusao: string;
  observacoes: string;
  /** Depois da primeira edição manual o texto é do perito e o seletor não o toca mais. */
  editada: boolean;
  /** O formulário aberto: a conclusão do perito é facultativa e começa recolhida. */
  aberta: boolean;
  /** O exame de onde o rascunho partiu (situação e grau apurado). */
  base: string;
}

const guardados = new Map<string, RascunhoDaDecisao>();

const credencial = (s: SessionState): string | null => s.token; // ux-neutrality:allow auth-token

let sessaoDosGuardados = credencial(useSessionStore.getState());
useSessionStore.subscribe((estado) => {
  if (credencial(estado) === sessaoDosGuardados) return;
  guardados.clear();
  sessaoDosGuardados = credencial(estado);
});

/** Para os testes: a tela recomeça sem rascunho. */
export function esquecerOsRascunhos(): void {
  guardados.clear();
}

function baseDoExame(ccb: ExameEletronicoCcbData): string {
  return `${ccb.exame?.status ?? ''}|${ccb.exame?.resultado?.grau_convergencia ?? ''}`;
}

function sugerida(ccb: ExameEletronicoCcbData, grau: GrauConvergencia | null): string {
  return grau === null ? '' : (ccb.conclusoes_sugeridas?.[grau] ?? '');
}

function novo(ccb: ExameEletronicoCcbData): RascunhoDaDecisao {
  const grau = ccb.exame?.resultado?.grau_convergencia ?? null;
  return {
    grau,
    justificativa: '',
    conclusao: sugerida(ccb, grau),
    observacoes: '',
    editada: false,
    aberta: false,
    base: baseDoExame(ccb),
  };
}

function inicial(ccb: ExameEletronicoCcbData): RascunhoDaDecisao {
  const salvo = guardados.get(ccb.source_document_id);
  return salvo !== undefined && salvo.base === baseDoExame(ccb) ? salvo : novo(ccb);
}

/** O estado editável do formulário e as transições que o mantêm coerente. */
export function useRascunhoDaDecisao(ccb: ExameEletronicoCcbData) {
  const chave = ccb.source_document_id;
  const base = baseDoExame(ccb);
  const sugeridas = JSON.stringify(ccb.conclusoes_sugeridas ?? {});
  const [r, setR] = useState<RascunhoDaDecisao>(() => inicial(ccb));
  const sessao = useToken();
  const sessaoDoFormulario = useRef(sessao);

  // A conta mudou com o formulário aberto: o texto da anterior não passa para a seguinte.
  useEffect(() => {
    if (sessaoDoFormulario.current === sessao) return;
    sessaoDoFormulario.current = sessao;
    setR(novo(ccb));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessao]);

  // O exame mudou de base (concluído, reaberto, verificado de novo): o rascunho recomeça. A
  // releitura que só traz um objeto novo com os mesmos dados não o toca.
  useEffect(() => {
    setR((atual) => (atual.base === base ? atual : novo(ccb)));
    // `ccb` entra pela base: o objeto muda a cada releitura sem mudar o exame.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  // Sem edição manual, o texto acompanha a redação padrão que o servidor manda.
  useEffect(() => {
    setR((atual) => (atual.editada ? atual : { ...atual, conclusao: sugerida(ccb, atual.grau) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sugeridas]);

  useEffect(() => {
    if (ccb.exame?.status === 'concluido') guardados.delete(chave);
    else guardados.set(chave, r);
  }, [chave, r, ccb.exame?.status]);

  const trocarGrau = useCallback(
    (grau: GrauConvergencia) =>
      setR((atual) => ({
        ...atual,
        grau,
        conclusao: atual.editada ? atual.conclusao : sugerida(ccb, grau),
      })),
    [ccb],
  );
  const restaurar = useCallback(
    () => setR((atual) => ({ ...atual, conclusao: sugerida(ccb, atual.grau), editada: false })),
    [ccb],
  );
  const editarConclusao = useCallback(
    (conclusao: string) => setR((atual) => ({ ...atual, conclusao, editada: true })),
    [],
  );
  const setJustificativa = useCallback(
    (justificativa: string) => setR((atual) => ({ ...atual, justificativa })),
    [],
  );
  const setObservacoes = useCallback(
    (observacoes: string) => setR((atual) => ({ ...atual, observacoes })),
    [],
  );
  const abrir = useCallback((aberta: boolean) => setR((atual) => ({ ...atual, aberta })), []);

  return {
    grauApurado: ccb.exame?.resultado?.grau_convergencia ?? null,
    ...r,
    trocarGrau,
    restaurar,
    editarConclusao,
    setJustificativa,
    setObservacoes,
    abrir,
  };
}
